/* eslint-disable react/prop-types */
import { FC, useEffect, useRef, useState } from "react";
import {
	Control,
	FormProvider,
	SubmitHandler,
	useController,
	useForm,
	useFormState
} from "react-hook-form";
import { ArrowLeft, Search, Upload, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "src/components/ui/button";
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl,
	FormMessage,
	useFormField
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import { Checkbox } from "src/components/ui/checkbox";
import isEmpty from "lodash.isempty";
import { EventFormValues } from "../../adapters/formValuesToEvent";
import { useAzureFileUpload } from "src/components/AssetUploaders/useAzureFileUpload";
import { AzureImage } from "src/components/AssetUploaders/AzureImage";
import GroupsAudienceCard from "src/components/GroupsAudienceCard/GroupsAudienceCard";
import useAdminGroups from "src/features/Groups/hooks/useAdminGroups";
import useAdminHashtag from "src/features/Hashtags/hooks/useAdminHashtag";
import useAdminHashtagsPaged from "src/features/Hashtags/hooks/useAdminHashtagsPaged/useAdminHashtagsPaged";
import { SEARCH_MIN_CHARS } from "src/constants";
import useDebounce from "src/hooks/useDebounce/useDebounce";
import RichTextEditor from "src/features/Announcements/components/RichTextEditor/RichTextEditor";

const TAG_DROPDOWN_PAGE_SIZE = 20;
const BODY_MAX_TEXT_LENGTH = 2000;

const htmlToPlainTextLength = (html: string): number => {
	if (!html) return 0;
	if (typeof window === "undefined" || !window.DOMParser) {
		return html.replace(/<[^>]*>/g, "").length;
	}
	const doc = new DOMParser().parseFromString(html, "text/html");
	return (doc.body.textContent ?? "").length;
};

const BodyRichTextEditor: FC<{
	value: string;
	onChange: (html: string) => void;
}> = ({ value, onChange }) => {
	// Inner component so useFormField can read the FormItem context to wire
	// id/aria-describedby/aria-invalid onto the editor's textbox.
	const { formItemId, formDescriptionId, formMessageId, error } =
		useFormField();
	const describedBy = error
		? `${formDescriptionId} ${formMessageId}`
		: formDescriptionId;
	return (
		<RichTextEditor
			id={formItemId}
			aria-describedby={describedBy}
			aria-invalid={!!error}
			content={value}
			onChange={onChange}
			placeholder="Event description"
		/>
	);
};

export type { EventFormValues };

interface Props {
	defaultValues: EventFormValues;
	onSubmit: (data: EventFormValues, imageRemoved: boolean) => void;
	isSubmitting?: boolean;
	isEdit?: boolean;
	onDelete?: () => void;
	existingImageReference?: string | null;
}

interface TagCardProps {
	control: Control<EventFormValues>;
}

const TagCard: FC<TagCardProps> = ({ control }) => {
	const [searchTerm, setSearchTerm] = useState("");
	const debouncedTerm = useDebounce(searchTerm, 300);
	const trimmed = debouncedTerm.trim();
	const hasEnoughChars = trimmed.length >= SEARCH_MIN_CHARS;

	const { data, isFetching } = useAdminHashtagsPaged(
		trimmed,
		1,
		TAG_DROPDOWN_PAGE_SIZE,
		undefined,
		{ enabled: hasEnoughChars }
	);

	const results = data?.results ?? [];

	return (
		<FormField
			control={control}
			name="tag"
			render={({ field }) => {
				return (
					<div className="rounded-xl border border-border bg-subtle shadow-sm">
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-1 block">
								Tag
							</label>
							<p className="text-[13px] text-muted-foreground mb-4">
								Link this event to a social feed hashtag
							</p>

							<div className="max-w-[480px]">
								{field.value ? (
									<SelectedHashtagChip
										uuid={field.value}
										onRemove={() => {
											field.onChange(null);
											setSearchTerm("");
										}}
									/>
								) : (
									<div>
										<div className="relative">
											<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
											<input
												type="text"
												value={searchTerm}
												onChange={(e) => setSearchTerm(e.target.value)}
												placeholder="Search hashtags..."
												aria-label="Search hashtags"
												className="w-full bg-white border border-border rounded-lg pl-9 pr-4 py-2 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
											/>
										</div>

										{searchTerm && !hasEnoughChars && (
											<p className="mt-2 text-xs text-muted-foreground">
												Type {SEARCH_MIN_CHARS} or more characters to search.
											</p>
										)}

										{hasEnoughChars && (
											<ul
												role="listbox"
												aria-label="Hashtag results"
												className="mt-2 bg-white border border-border rounded-lg shadow-md max-h-60 overflow-y-auto"
											>
												{results.length === 0 && !isFetching && (
													<li
														role="presentation"
														className="px-3 py-2.5 text-sm text-muted-foreground"
													>
														No hashtags found
													</li>
												)}
												{results.map((hashtag) => (
													<li
														key={hashtag.uuid}
														role="option"
														aria-selected={false}
														tabIndex={0}
														className="px-3 py-2.5 cursor-pointer hover:bg-primary-lighter text-sm text-foreground transition-colors first:rounded-t-lg last:rounded-b-lg focus:bg-primary-lighter focus:outline-none"
														onClick={() => {
															field.onChange(hashtag.uuid);
															setSearchTerm("");
														}}
														onKeyDown={(e) => {
															if (e.key === "Enter" || e.key === " ") {
																e.preventDefault();
																field.onChange(hashtag.uuid);
																setSearchTerm("");
															}
														}}
													>
														#{hashtag.name}
													</li>
												))}
											</ul>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				);
			}}
		/>
	);
};

interface SelectedHashtagChipProps {
	uuid: string;
	onRemove: () => void;
}

const SelectedHashtagChip: FC<SelectedHashtagChipProps> = ({
	uuid,
	onRemove
}) => {
	const { data: hashtag } = useAdminHashtag(uuid);

	return (
		<div className="flex items-center gap-2">
			<div className="flex-1 flex items-center bg-white border border-border rounded-lg px-3 py-2 shadow-xs">
				<span className="text-muted-foreground mr-1">#</span>
				<span className="text-sm text-foreground font-medium">
					{hashtag?.name ?? "…"}
				</span>
			</div>
			<button
				type="button"
				className="h-9 w-9 flex items-center justify-center rounded-lg border border-border bg-white hover:bg-muted transition-colors shadow-xs"
				aria-label="Remove tag"
				onClick={onRemove}
			>
				<X className="h-4 w-4 text-muted-foreground" />
			</button>
		</div>
	);
};

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const MAX_IMAGE_SIZE = 10_485_760; // 10 MB

const EventForm: FC<Props> = ({
	defaultValues,
	onSubmit,
	isSubmitting = false,
	isEdit = false,
	onDelete,
	existingImageReference
}) => {
	const methods = useForm<EventFormValues>({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues
	});

	const { reset, control, handleSubmit, watch, setValue, trigger } = methods;
	const { isDirty, errors } = useFormState({ control });

	// Don't pass `defaultValue` here — react-hook-form would treat the literal
	// `[]` as the registered default and could override a non-empty value
	// supplied via `defaultValues.audienceGroupIds` on the edit page. The form
	// already provides the correct default through `useForm({ defaultValues })`.
	const { field: audienceGroupIds } = useController({
		control,
		name: "audienceGroupIds"
	});
	const {
		data: adminGroups = [],
		isLoading: groupsLoading,
		isError: groupsError
	} = useAdminGroups();

	const fileInputRef = useRef<HTMLInputElement>(null);
	const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
	const [imageRemoved, setImageRemoved] = useState(false);
	const [imageError, setImageError] = useState<string | null>(null);
	const [isDragging, setIsDragging] = useState(false);

	const imageReference = watch("image");

	const {
		upload,
		uploading: isUploading,
		error: uploadError
	} = useAzureFileUpload((data) => {
		if (Array.isArray(data) && typeof data[0] === "string") {
			setValue("image", data[0], { shouldDirty: true });
		}
	});

	useEffect(() => {
		if (uploadError) setImageError("Failed to upload image, try again.");
	}, [uploadError]);

	const hasImage =
		!imageRemoved &&
		(!!imageReference || !!previewBlobUrl || !!existingImageReference);

	const isSubmitBtnDisabled =
		(!isDirty && !imageRemoved) ||
		!isEmpty(errors) ||
		isSubmitting ||
		isUploading ||
		!!imageError;

	useEffect(() => {
		reset(defaultValues);
		setImageRemoved(false);
		setImageError(null);
		setPreviewBlobUrl((prev) => {
			if (prev) URL.revokeObjectURL(prev);
			return null;
		});
	}, [defaultValues]);

	const startDate = watch("startDate");
	const startTime = watch("startTime");
	const endTime = watch("endTime");

	useEffect(() => {
		trigger("endDate");
	}, [startDate, startTime, endTime, trigger]);

	useEffect(() => {
		if (imageReference && previewBlobUrl) {
			URL.revokeObjectURL(previewBlobUrl);
			setPreviewBlobUrl(null);
		}
	}, [imageReference, previewBlobUrl]);

	const validateAndSetFile = (file: File) => {
		if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
			setImageError("Only JPG and PNG files are allowed.");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		if (file.size > MAX_IMAGE_SIZE) {
			setImageError("Image must be under 10 MB.");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		setImageError(null);
		if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
		setPreviewBlobUrl(URL.createObjectURL(file));
		setImageRemoved(false);
		upload(file, "images", undefined, "admin-content-image");
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) validateAndSetFile(file);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files[0];
		if (file) validateAndSetFile(file);
	};

	const handleRemoveImage = () => {
		setValue("image", null, { shouldDirty: true });
		if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
		setPreviewBlobUrl(null);
		setImageRemoved(true);
		setImageError(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const renderImagePreview = () => {
		if (previewBlobUrl) {
			return (
				<img
					src={previewBlobUrl}
					alt="Uploaded image preview"
					className="object-contain w-full h-auto max-h-[400px]"
				/>
			);
		}
		const ref = imageReference ?? existingImageReference;
		if (ref) {
			return (
				<AzureImage
					uuid={ref}
					imageProfile="admin-content-image"
					alt="Uploaded image preview"
					className="object-contain w-full h-auto max-h-[400px]"
				/>
			);
		}
		return null;
	};

	return (
		<form
			onSubmit={handleSubmit((data) => onSubmit(data, imageRemoved))}
			onKeyDown={(e) => {
				if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
					e.preventDefault();
				}
			}}
		>
			<FormProvider {...methods}>
				<div className="flex flex-col gap-4">
					{/* Details */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							errors.name ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{errors.name && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									Please fill out the required field
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Details
							</label>

							<div className="flex flex-col gap-5">
								<FormField
									control={control}
									name="name"
									rules={{
										required: "Name is required",
										maxLength: {
											value: 100,
											message: "Maximum 100 characters"
										}
									}}
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												Name
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Event name"
													maxLength={100}
													className="w-full bg-white"
													{...field}
												/>
											</FormControl>
											<p className="text-[13px] text-zinc-600 text-right mt-1">
												{field.value?.length || 0} / 100
											</p>
										</FormItem>
									)}
								/>

								<FormField
									control={control}
									name="location"
									rules={{
										maxLength: {
											value: 200,
											message: "Maximum 200 characters"
										}
									}}
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												Location
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Venue name"
													maxLength={200}
													className="w-full bg-white"
													{...field}
												/>
											</FormControl>
											<p className="text-[13px] text-zinc-600 text-right mt-1">
												{field.value?.length || 0} / 200
											</p>
										</FormItem>
									)}
								/>

								<FormField
									control={control}
									name="body"
									rules={{
										validate: (value: string | null | undefined) =>
											htmlToPlainTextLength(value ?? "") <=
												BODY_MAX_TEXT_LENGTH ||
											`Maximum ${BODY_MAX_TEXT_LENGTH} characters`
									}}
									render={({ field }) => {
										const textLength = htmlToPlainTextLength(field.value ?? "");
										return (
											<FormItem>
												<FormLabel className="text-sm font-medium text-zinc-600">
													Description
												</FormLabel>
												<BodyRichTextEditor
													value={field.value ?? ""}
													onChange={field.onChange}
												/>
												<div className="flex justify-between items-start mt-1">
													<FormMessage />
													<p
														className={[
															"text-[13px] ml-auto",
															textLength > BODY_MAX_TEXT_LENGTH
																? "text-red-600"
																: "text-zinc-600"
														].join(" ")}
													>
														{textLength} / {BODY_MAX_TEXT_LENGTH}
													</p>
												</div>
											</FormItem>
										);
									}}
								/>
							</div>
						</div>
					</div>

					{/* Schedule */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							errors.startDate || errors.endDate
								? "border-red-400/60"
								: "border-border"
						].join(" ")}
					>
						{(errors.startDate || errors.endDate) && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									{errors.endDate?.message ||
										"Please fill out the required fields"}
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Schedule
							</label>

							<div className="flex flex-col gap-5">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<FormField
										control={control}
										name="startDate"
										rules={{
											required: "Start date is required"
										}}
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-sm font-medium text-zinc-600">
													Start date
												</FormLabel>
												<FormControl>
													<Input
														type="date"
														className="w-full bg-white"
														{...field}
													/>
												</FormControl>
											</FormItem>
										)}
									/>

									<FormField
										control={control}
										name="startTime"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-sm font-medium text-zinc-600">
													Start time
												</FormLabel>
												<FormControl>
													<Input
														type="time"
														className="w-full bg-white"
														{...field}
													/>
												</FormControl>
											</FormItem>
										)}
									/>
								</div>

								<FormField
									control={control}
									name="showStartDate"
									render={({ field }) => (
										<FormItem className="flex items-center gap-2">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											<FormLabel className="!mt-0 text-sm text-zinc-700 cursor-pointer">
												Show start date to app users
											</FormLabel>
										</FormItem>
									)}
								/>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<FormField
										control={control}
										name="endDate"
										rules={{
											required: "End date is required",
											validate: (value: string) => {
												const startDate = watch("startDate");
												const startTime = watch("startTime");
												const endTime = watch("endTime");
												if (startDate && value) {
													const start = new Date(
														`${startDate}T${startTime || "00:00"}`
													);
													const end = new Date(
														`${value}T${endTime || "00:00"}`
													);
													if (end <= start) {
														return "End date must be after start date";
													}
												}
												return true;
											}
										}}
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-sm font-medium text-zinc-600">
													End date
												</FormLabel>
												<FormControl>
													<Input
														type="date"
														className="w-full bg-white"
														min={
															!isEdit
																? new Date().toISOString().split("T")[0]
																: undefined
														}
														{...field}
													/>
												</FormControl>
											</FormItem>
										)}
									/>

									<FormField
										control={control}
										name="endTime"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-sm font-medium text-zinc-600">
													End time
												</FormLabel>
												<FormControl>
													<Input
														type="time"
														className="w-full bg-white"
														{...field}
													/>
												</FormControl>
											</FormItem>
										)}
									/>
								</div>

								<FormField
									control={control}
									name="showEndDate"
									render={({ field }) => (
										<FormItem className="flex items-center gap-2">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											<FormLabel className="!mt-0 text-sm text-zinc-700 cursor-pointer">
												Show end date to app users
											</FormLabel>
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>

					{/* Tag */}
					<TagCard control={control} />

					{/* Image */}
					<div className="rounded-xl border border-border bg-subtle shadow-sm">
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Image (optional)
							</label>
							<p className="text-[13px] text-muted-foreground mb-4">
								JPG or PNG, 1000 x 566px recommended
							</p>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/jpeg,image/jpg,image/png"
								className="hidden"
								onChange={handleFileChange}
							/>
							<div className="max-w-[480px]">
								{hasImage ? (
									<>
										<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[400px]">
											{renderImagePreview()}
										</div>
										{isUploading && (
											<p className="text-xs text-muted-foreground mb-2">
												Uploading…
											</p>
										)}
										<div className="flex items-center gap-2">
											<button
												type="button"
												className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
												onClick={() => fileInputRef.current?.click()}
											>
												Change image
											</button>
											<button
												type="button"
												className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
												onClick={handleRemoveImage}
											>
												Remove
											</button>
										</div>
									</>
								) : (
									<div
										role="button"
										tabIndex={0}
										onClick={() => fileInputRef.current?.click()}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												fileInputRef.current?.click();
											}
										}}
										onDragOver={handleDragOver}
										onDragLeave={handleDragLeave}
										onDrop={handleDrop}
										className={[
											"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer",
											isDragging
												? "border-primary bg-primary-lighter"
												: "border-border bg-white hover:border-primary-light hover:bg-primary-lighter/50"
										].join(" ")}
									>
										<Upload className="h-6 w-6 text-muted-foreground" />
										<div>
											<p className="text-sm font-medium text-foreground">
												Drag image here or click to browse
											</p>
											<p className="text-xs text-muted-foreground mt-1">
												JPG or PNG
											</p>
										</div>
									</div>
								)}
							</div>
							{imageError && (
								<p className="text-sm text-red-500 mt-2">{imageError}</p>
							)}
						</div>
					</div>

					{/* Groups audience */}
					<GroupsAudienceCard
						groupIds={audienceGroupIds.value ?? []}
						onChange={audienceGroupIds.onChange}
						groups={adminGroups.map((g) => ({
							id: Number(g.id),
							name: g.name
						}))}
						isLoading={groupsLoading}
						isError={groupsError}
					/>
				</div>

				<div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
					<div className="flex items-center gap-4">
						<Link
							to="/app/events"
							className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-foreground transition-colors"
						>
							<ArrowLeft className="h-4 w-4" />
							Back to events
						</Link>

						{isEdit && onDelete && (
							<Button type="button" variant="destructive" onClick={onDelete}>
								Delete
							</Button>
						)}
					</div>

					<Button type="submit" disabled={isSubmitBtnDisabled}>
						{isSubmitting ? (
							<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
						) : (
							"Save"
						)}
					</Button>
				</div>
			</FormProvider>
		</form>
	);
};

export default EventForm;
